import { getSessionEpoch } from './auth/session';
import { registerTaskOperation } from './store/taskOperations';
import { notifyTaskChange } from '../../app/api/notifyTaskChange';

const changeKey = 'timetask:timer-change';

// Same-origin tabs can invalidate their cache even while SignalR is reconnecting.
export function publishTimerChange() {
    const owner = localStorage.getItem('userID');
    if (!owner) return;
    try {
        localStorage.setItem(changeKey, JSON.stringify({owner, epoch: getSessionEpoch(),
            id: Date.now().toString(36) + '-' + Math.random().toString(36).slice(2)}));
    } catch { /* Restricted storage must not fail a confirmed timer command. */ }
}
export function subscribeTimerChanges(refresh) {
    const owner = localStorage.getItem('userID');
    const epoch = getSessionEpoch();
    const receive = event => {
        if (event.key !== changeKey || !event.newValue) return;
        try {
            const message = JSON.parse(event.newValue);
            if (message && typeof message.id === 'string' && owner && message.owner === owner && message.epoch === epoch &&
                owner === localStorage.getItem('userID') && epoch === getSessionEpoch()) refresh();
        } catch { /* Ignore malformed messages; no timer state is accepted from storage. */ }
    };
    window.addEventListener('storage', receive);
    return () => window.removeEventListener('storage', receive);
}

// Only invalidations are retried, never the timer mutation or the RunTime POST.
export function createTimerNotifier(timerHub, updateHub, onOwnEvent) {
    const owner = localStorage.getItem('userID');
    const epoch = getSessionEpoch();
    let pending;
    let lastVersion = 0;
    let sending = false;
    let disposed = false;
    let retry;
    const current = () => !disposed && !!owner && owner === localStorage.getItem('userID') && epoch === getSessionEpoch();
    const schedule = () => {
        if (current() && !retry) retry = setTimeout(() => { retry = undefined; flush(); }, 1000);
    };
    const flush = async () => {
        if (!current() || sending || !pending) return;
        if (retry) { clearTimeout(retry); retry = undefined; }
        const message = pending;
        sending = true;
        const deliveries = [];
        if (!message.timerSent && timerHub?.state === 'Connected') {
            onOwnEvent(JSON.stringify(message.event));
            deliveries.push(Promise.resolve().then(() => timerHub.invoke('Send', owner, ...message.event))
                .then(() => { message.timerSent = true; }));
        }
        if (!message.updateSent && updateHub?.state === 'Connected') {
            deliveries.push(notifyTaskChange(updateHub, owner, message.operationId)
                .then(() => { message.updateSent = true; }));
        }
        await Promise.allSettled(deliveries);
        sending = false;
        if (!current()) return;
        if (pending !== message) { flush(); return; }
        if (message.timerSent && message.updateSent) pending = undefined;
        else schedule();
    };
    return {
        announce(version, entry) {
            if (!current() || version <= lastVersion) return;
            lastVersion = version;
            const event = entry && Number.isSafeInteger(Number(entry.taskId))
                ? [Number(entry.taskId), entry.startTime ?? Date.now(), entry.isTimerActive] : null;
            pending = {version, event, operationId: registerTaskOperation(),
                timerSent: !event || !timerHub, updateSent: !updateHub};
            flush();
        },
        flush,
        stop() { disposed = true; if (retry) clearTimeout(retry); },
    };
}
